import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, Chip, Checkbox } from "@material-ui/core";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import PersonIcon from "@material-ui/icons/Person";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import InfoIcon from "@material-ui/icons/Info";
import AnchoredFloatingPanel from "../../common/AnchoredFloatingPanel";
import { getAddressInfo } from "mocks/SharedTransform/response.transforms";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import { useStyles } from "./ClubbingSuggestion.styles";
import { TextConstants } from "../../constants/TextConstants";
import { ClubbingSuggestionProps, InfoRowProps } from "./ClubbingSuggestion.types";

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, valueClassName }) => {
  const classes = useStyles();
  return (
    <Box className={classes.infoRow}>
      {icon}
      <span className={classes.label}>{label}:</span>
      <span className={valueClassName || classes.value}>{value}</span>
    </Box>
  );
};

const ClubbingSuggestionPanel: React.FC<{ badgeCount: number; children: React.ReactNode }> = ({ badgeCount, children }) => (
  <AnchoredFloatingPanel
    width="360px"
    maxHeight="70vh"
    toggleIcon={<InfoIcon />}
    toggleLabel={TextConstants.CLUBBING}
    badgeCount={badgeCount}
    defaultOpen={true}
  >
    {children}
  </AnchoredFloatingPanel>
);

const PanelHeader: React.FC = () => {
  const classes = useStyles();
  return (
    <Box className={classes.header}>
      <Typography className={classes.title}>{TextConstants.ORDER_CLUBBING_SUGGESTION}</Typography>
    </Box>
  );
};

const PanelContent: React.FC<{ message: string }> = ({ message }) => {
  const classes = useStyles();
  return (
    <Box className={classes.content}>
      <Typography className={classes.emptyState}>{message}</Typography>
    </Box>
  );
};

interface SummaryItemProps {
  label: string;
  value: string | number;
  align: 'left' | 'center' | 'right';
}

const SummaryItem: React.FC<SummaryItemProps> = ({ label, value, align }) => {
  const classes = useStyles();
  const alignClass = align === 'left' ? classes.summaryItemLeft : 
                     align === 'center' ? classes.summaryItemCenter : 
                     classes.summaryItemRight;

  return (
    <Box className={`${classes.summaryItem} ${alignClass}`}>
      <Typography variant="caption" color="textSecondary" className={classes.summaryLabel}>
        {label}
      </Typography>
      <Typography variant="body2" className={classes.summaryValue} style={{textAlign: 'center'}}>
        {value}
      </Typography>
    </Box>
  );
};

const ClubbingSuggestion: React.FC<ClubbingSuggestionProps> = ({ suggestionData, loading, onSelectionChange }) => {
  const classes = useStyles();
  const orders = suggestionData?.orders || [];
  const [selectedConsignments, setSelectedConsignments] = useState<string[]>([]);

  // Initialize all orders as selected when suggestionData changes
  useEffect(() => {
    const allConsignmentCodes = orders.map((order: any) => order.consignmentCode);
    setSelectedConsignments(allConsignmentCodes);
  }, [suggestionData?.orders]);

  // Notify parent component of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedConsignments);
    }
  }, [selectedConsignments, onSelectionChange]);

  const handleCheckboxChange = useCallback((consignmentCode: string) => {
    setSelectedConsignments((prev) => {
      const isSelected = prev.includes(consignmentCode);
      if (isSelected) {
        // Remove from array
        return prev.filter(code => code !== consignmentCode);
      } else {
        // Add back at original position
        const originalIndex = orders.findIndex((order: any) => order.consignmentCode === consignmentCode);
        const newSelection = [...prev];
        
        // Find correct position to insert based on original order
        let insertIndex = 0;
        for (let i = 0; i < originalIndex; i++) {
          if (newSelection.includes(orders[i].consignmentCode)) {
            insertIndex++;
          }
        }
        newSelection.splice(insertIndex, 0, consignmentCode);
        return newSelection;
      }
    });
  }, [orders]);

  const summaryData: SummaryItemProps[] = [
    { label: TextConstants.TOTAL_ITEMS, value: suggestionData?.totalItems || 0, align: 'left' },
    { label: TextConstants.SUGGESTED_VEHICLE, value: suggestionData?.vehicleType || '', align: 'center' },
    { label: TextConstants.TOTAL_WEIGHT, value: `${suggestionData?.totalWeight || 0} kg`, align: 'right' },
  ];

  if (loading) {
    return (
      <ClubbingSuggestionPanel badgeCount={0}>
        <PanelHeader />
        <PanelContent message={TextConstants.LOADING_SUGGESTIONS} />
      </ClubbingSuggestionPanel>
    );
  }

  if (!orders.length) {
    return (
      <ClubbingSuggestionPanel badgeCount={0}>
        <PanelHeader />
        <PanelContent message={TextConstants.NO_CLUBBING_SUGGESTIONS} />
      </ClubbingSuggestionPanel>
    );
  }

  return (
    <ClubbingSuggestionPanel badgeCount={orders.length}>
      <PanelHeader />
      <Box className={classes.content}>
        <Box className={classes.summaryBox}>
          {summaryData.map((item, index) => (
            <SummaryItem key={index} {...item} />
          ))}
        </Box>

        {orders.map((order) => {
          const hasAssignee = Boolean(order.assignee?.name);
          const isSelected = selectedConsignments.includes(order.consignmentCode);
          
          return (
            <Paper 
              key={order.consignmentCode} 
              className={classes.suggestionCard}
              elevation={0}
              style={{ opacity: isSelected ? 1 : 0.8 }}
            >
              <Box className={classes.orderHeader}>
                <Box style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography className={classes.consignmentId}>{order.consignmentCode}</Typography>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleCheckboxChange(order.consignmentCode)}
                    color="primary"
                    size="medium"
                    style={{ marginLeft: 8 }}
                  />
                </Box>
                <Box>
                  <Chip label={`#${order.sequence}`} size="small" className={classes.chip} color="primary" />
                  {order.zone && (
                    <Chip label={order.zone} size="small" className={classes.chip} style={{ marginLeft: 4 }} color="default" />
                  )}
                </Box>
              </Box>

              <InfoRow 
                icon={<PersonIcon className={classes.icon} />}
                label={TextConstants.DRIVER}
                value={order.assignee?.name || TextConstants.NOT_ASSIGNED}
                valueClassName={hasAssignee ? classes.value : classes.noDriver}
              />

              <InfoRow 
                icon={<LocalShippingIcon className={classes.icon} />}
                label={TextConstants.VEHICLE}
                value={order.assignee?.vehicleType || "---"}
                valueClassName={hasAssignee ? classes.value : classes.noDriver}
              />
              <Box className={classes.itemsWeightRow}>
                <Box className={classes.infoRow} style={{ marginBottom: 0 }}>
                  <ShoppingCartIcon className={classes.icon} />
                  <span className={classes.label}>{TextConstants.ITEMS}:</span>
                  <span className={classes.value}>{order?.items || 0}</span>
                </Box>
                <span className={classes.separator}>|</span>
                <Box className={classes.infoRow} style={{ marginBottom: 0 }}>
                  <span className={classes.label}>{TextConstants.WEIGHT}:</span>
                  <span className={classes.value}>{`${order?.weight || 0} kg`}</span>
                </Box>
              </Box>
              <Box className={classes.infoRow}>
                  <LocationOnIcon className={classes.icon} />
                  <Box className={classes.addressContainer}>
                    <Box>
                      <span className={classes.label}>{TextConstants.ADDRESS}</span>
                      <span className={classes.value}>{getAddressInfo(order.address)}</span>
                    </Box>
                  </Box>
                </Box>
            </Paper>
          );
        })}
      </Box>
    </ClubbingSuggestionPanel>
  );
};

export default React.memo(ClubbingSuggestion);
